/**
 * client/skins.ts
 * Responsabilidad: Definición y dibujado procedimental de las skins de jugador.
 *
 * Cada skin es una función pura que recibe el contexto, la posición, el radio, el color
 * del elemento y el tiempo actual, y dibuja el cuerpo del jugador (sin bordes genéricos).
 */
import type { SkinId } from "./types";
import {
    drawTattooLayer,
    drawFaceLayer,
    drawAuraLayer,
    drawElementalBody,
    drawThrusterPreview,
    getElementalPalette,
    sanitizeCosmetics,
    getBotCosmetics,
    type PlayerCosmeticsConfig,
    type EyeStyle,
    type MouthStyle,
    type GlassesStyle,
    type TattooStyle,
    type AuraStyle,
    type ThrusterStyle,
    DEFAULT_COSMETICS,
} from "./cosmetics";

export type { SkinId, PlayerCosmeticsConfig, EyeStyle, MouthStyle, GlassesStyle, TattooStyle, AuraStyle, ThrusterStyle };
export { DEFAULT_COSMETICS, sanitizeCosmetics, getBotCosmetics, drawElementalBody, drawThrusterPreview, getElementalPalette };

/**
 * Dibuja la skin modular en capas sobre el cuerpo elemental:
 * 1. drawTattooLayer
 * 2. drawFaceLayer (Ojos + Gafas + Boca alineados)
 * 3. drawAuraLayer (Corona, Cuernos, Espinas, Alas, etc., cromoadaptadas al elemento)
 */
export function drawModularSkin(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    cosmetics: PlayerCosmeticsConfig | undefined,
    sessionId: string = "local",
    facingAngle: number = 0,
    nowMs: number = 0,
    element: string = "water"
): void {
    const safeCosmetics = cosmetics ? sanitizeCosmetics(cosmetics) : getBotCosmetics(sessionId);
    drawTattooLayer(ctx, x, y, radius, safeCosmetics.tattoo, nowMs);
    drawFaceLayer(
        ctx,
        x,
        y,
        radius,
        safeCosmetics.eyes,
        safeCosmetics.glasses,
        safeCosmetics.mouth,
        facingAngle,
        nowMs
    );
    drawAuraLayer(ctx, x, y, radius, safeCosmetics.aura, nowMs, element);
}

/** Lista inmutable de skins en orden de ciclado */
export const SKIN_IDS: readonly SkinId[] = [
    "default",
    "ring",
    "dotted",
    "metallic",
    "eyes",
    "smile",
] as const;

/** Color del borde por defecto para el jugador local */
export const LOCAL_BORDER_COLOR = "#ffffff";

/**
 * Dibuja el cuerpo de un jugador según su skin.
 * NO dibuja el borde local (eso lo hace el renderer).
 * @param skin  Skin a dibujar.
 * @param ctx   Contexto 2D.
 * @param x     Coordenada X en pantalla (centro).
 * @param y     Coordenada Y en pantalla (centro).
 * @param radius Radio del círculo.
 * @param color Color base del elemento (hex).
 * @param facingAngle Ángulo hacia el que mira el jugador (radianes). Usado por eyes/smile.
 */
export function drawPlayerSkin(
    skin: SkinId,
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    color: string,
    facingAngle: number
): void {
    switch (skin) {
        case "default":
            drawDefault(ctx, x, y, radius, color);
            break;
        case "ring":
            drawRing(ctx, x, y, radius, color);
            break;
        case "dotted":
            drawDotted(ctx, x, y, radius, color);
            break;
        case "metallic":
            drawMetallic(ctx, x, y, radius, color);
            break;
        case "eyes":
            drawEyes(ctx, x, y, radius, color, facingAngle);
            break;
        case "smile":
            drawSmile(ctx, x, y, radius, color, facingAngle);
            break;
    }
}

/** Skin por defecto: círculo sólido con el color del elemento. */
function drawDefault(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, radius: number, color: string
): void {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
}

/** Skin ring: círculo con un anillo interior del mismo color. */
function drawRing(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, radius: number, color: string
): void {
    // Cuerpo base
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    // Anillo interior (oscurecido)
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.6, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(0, 0, 0, 0.3)";
    ctx.lineWidth = Math.max(2, radius * 0.08);
    ctx.stroke();
}

/** Skin dotted: borde punteado permanente. */
function drawDotted(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, radius: number, color: string
): void {
    // Cuerpo base
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    // Borde punteado
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.arc(x, y, radius - 1, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.7)";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.setLineDash([]);
}

/** Skin metallic: gradiente radial con punto de brillo cenital. */
function drawMetallic(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, radius: number, color: string
): void {
    const gradient = ctx.createRadialGradient(
        x - radius * 0.3,
        y - radius * 0.3,
        radius * 0.1,
        x,
        y,
        radius
    );
    gradient.addColorStop(0, "rgba(255, 255, 255, 0.9)");
    gradient.addColorStop(0.4, color);
    gradient.addColorStop(1, color);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
}

/** Skin eyes: dos ojos con pupilas orientadas hacia facingAngle. */
function drawEyes(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, radius: number, color: string, facingAngle: number
): void {
    // Cuerpo base
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    // Dibujar dos ojos
    const eyeOffsetX = Math.cos(facingAngle + Math.PI / 2) * radius * 0.35;
    const eyeOffsetY = Math.sin(facingAngle + Math.PI / 2) * radius * 0.35;
    const pupilOffsetX = Math.cos(facingAngle) * radius * 0.12;
    const pupilOffsetY = Math.sin(facingAngle) * radius * 0.12;
    const eyeRadius = radius * 0.18;
    const pupilRadius = radius * 0.08;
    for (const sign of [-1, 1]) {
        const ex = x + eyeOffsetX * sign;
        const ey = y + eyeOffsetY * sign;
        // Blanco del ojo
        ctx.beginPath();
        ctx.arc(ex, ey, eyeRadius, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        // Pupila
        ctx.beginPath();
        ctx.arc(ex + pupilOffsetX, ey + pupilOffsetY, pupilRadius, 0, Math.PI * 2);
        ctx.fillStyle = "#000000";
        ctx.fill();
    }
}

/** Skin smile: ojos + sonrisa curva. */
function drawSmile(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, radius: number, color: string, facingAngle: number
): void {
    // Primero dibuja los ojos (reutiliza la lógica)
    drawEyes(ctx, x, y, radius, color, facingAngle);
    // Luego la sonrisa: arco debajo de los ojos
    const smileCenterX = x;
    const smileCenterY = y + radius * 0.15;
    const smileRadius = radius * 0.4;
    ctx.beginPath();
    ctx.arc(smileCenterX, smileCenterY, smileRadius, 0.15 * Math.PI, 0.85 * Math.PI);
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = Math.max(1.5, radius * 0.06);
    ctx.lineCap = "round";
    ctx.stroke();
    ctx.lineCap = "butt"; // Reset
}

export function drawSkinOverlay(
    skin: SkinId,
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    facingAngle: number
): void {
    switch (skin) {
        case "default":
            break;
        case "ring": {
            ctx.beginPath();
            ctx.arc(x, y, radius * 0.58, 0, Math.PI * 2);
            ctx.strokeStyle = "rgba(0, 0, 0, 0.35)";
            ctx.lineWidth = Math.max(2, radius * 0.08);
            ctx.stroke();
            break;
        }
        case "dotted": {
            ctx.save();
            ctx.setLineDash([4, 4]);
            ctx.beginPath();
            ctx.arc(x, y, radius * 0.88, 0, Math.PI * 2);
            ctx.strokeStyle = "rgba(255, 255, 255, 0.75)";
            ctx.lineWidth = 2;
            ctx.stroke();
            ctx.restore();
            break;
        }
        case "metallic": {
            const gloss = ctx.createRadialGradient(
                x - radius * 0.3,
                y - radius * 0.3,
                radius * 0.05,
                x - radius * 0.2,
                y - radius * 0.2,
                radius * 0.75
            );
            gloss.addColorStop(0, "rgba(255, 255, 255, 0.65)");
            gloss.addColorStop(1, "rgba(255, 255, 255, 0)");
            ctx.beginPath();
            ctx.arc(x, y, radius * 0.85, 0, Math.PI * 2);
            ctx.fillStyle = gloss;
            ctx.fill();
            break;
        }
        case "eyes":
        case "smile": {
            const eyeOffsetX = Math.cos(facingAngle + Math.PI / 2) * radius * 0.32;
            const eyeOffsetY = Math.sin(facingAngle + Math.PI / 2) * radius * 0.32;
            const pupilOffsetX = Math.cos(facingAngle) * radius * 0.11;
            const pupilOffsetY = Math.sin(facingAngle) * radius * 0.11;
            const eyeRadius = radius * 0.17;
            const pupilRadius = radius * 0.08;
            for (const sign of [-1, 1]) {
                const ex = x + eyeOffsetX * sign;
                const ey = y + eyeOffsetY * sign;
                ctx.beginPath();
                ctx.arc(ex, ey, eyeRadius, 0, Math.PI * 2);
                ctx.fillStyle = "#ffffff";
                ctx.fill();
                ctx.lineWidth = Math.max(1, radius * 0.03);
                ctx.strokeStyle = "rgba(0,0,0,0.5)";
                ctx.stroke();

                ctx.beginPath();
                ctx.arc(ex + pupilOffsetX, ey + pupilOffsetY, pupilRadius, 0, Math.PI * 2);
                ctx.fillStyle = "#000000";
                ctx.fill();
            }
            if (skin === "smile") {
                const smileX = x + Math.cos(facingAngle) * (radius * 0.08);
                const smileY = y + Math.sin(facingAngle) * (radius * 0.08);
                ctx.beginPath();
                ctx.arc(
                    smileX,
                    smileY,
                    radius * 0.36,
                    facingAngle - Math.PI * 0.35,
                    facingAngle + Math.PI * 0.35
                );
                ctx.strokeStyle = "#000000";
                ctx.lineWidth = Math.max(1.5, radius * 0.06);
                ctx.lineCap = "round";
                ctx.stroke();
                ctx.lineCap = "butt";
            }
            break;
        }
    }
}

